import { inject, Injectable } from '@angular/core';
import {
    AiEditChange,
    AiEditRequestDto,
    AiEditStatusEvent,
    DocumentItemPartial,
    DocumentItemTypeEnum,
    DocumentSessionPartial,
    UserController,
} from '@domain';
import { DocumentSessionTypeEnum } from 'src/app/domain/enums/documents/document-session-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_CALLOUT_COLOR } from 'src/app/modules/document/callout-colors';
import { DEFAULT_TABLE_STYLE, DEFAULT_TABLE_ZEBRA } from 'src/app/modules/document/table-options';
import { PusherService } from 'src/app/shared/utils/pusher/pusher.service';
import { EditDataService } from '../_edit-data.service';

/** Uma alteração do roteiro: o que mostrar no checklist, como aplicar e como desfazer. */
interface MockStep {
    change: AiEditChange;
    apply: () => void;
    revert: () => void;
}

/** Ritmo do roteiro, próximo do protótipo (um pedido de 3 alterações leva ~7 s). */
const MOCK_TIMING = {
    planning: 500,
    firstChange: 2200,
    beforeApply: 1100,
    betweenChanges: 500,
    done: 500,
    revertStep: 350,
} as const;

const mockId = (): string =>
    (crypto.randomUUID?.() ?? `${Date.now()}${Math.random()}`).replace(/-/g, '').slice(0, 24);

/**
 * Mock LOCAL da API do modo "Editar com IA", enquanto `POST document/ai-edit` não existe
 * (ligado por `environment.AI_EDIT_MOCK`, ver `AiEditMockInterceptor`).
 *
 * Simula o que o backend vai fazer (docs/EDITOR-CHAT-IA.md, seção 4.2) com o documento
 * ABERTO: monta um plano de até 3 alterações (encurtar um texto, criar texto + tabela de
 * etapas, renomear a seção) e as emite pelo `PusherService`, nos MESMOS eventos que o
 * backend emitirá (`ai-edit-status`, `field-updated`, `fields-created`,
 * `session-name-updated`). Assim a UI inteira roda pelo caminho real do realtime.
 *
 * As alterações são só locais: recarregar a página volta o documento ao que está salvo.
 * O Desfazer (`revert`) emite os eventos inversos.
 */
@Injectable({ providedIn: 'root' })
export class AiEditMockService {
    private _pusher = inject(PusherService);
    private _editData = inject(EditDataService);
    private _userController = inject(UserController);
    private _translate = inject(TranslateService);

    private _runs = new Map<string, { channel: string; steps: MockStep[] }>();
    private _timers: ReturnType<typeof setTimeout>[] = [];

    /** Começa um pedido e devolve o `taskId` (o "202"). Os eventos vêm em seguida. */
    public start(request: AiEditRequestDto): string {
        const taskId = `mock-${mockId()}`;
        const channel = `DOCUMENT-${request.documentId}`;
        const userId = this._currentUserId();
        const steps = this._plan();
        this._runs.set(taskId, { channel, steps });

        const status = (event: Omit<AiEditStatusEvent, 'taskId' | 'userId'>): void =>
            this._pusher.simulate(channel, 'ai-edit-status', { taskId, userId, ...event });

        const sessions = this._contentSessions().length;
        let at = MOCK_TIMING.planning;
        this._later(at, () =>
            status({
                stage: 'planning',
                message: this._t(sessions === 1 ? 'mock.planningOne' : 'mock.planning', {
                    count: sessions,
                }),
            })
        );

        at += MOCK_TIMING.firstChange;
        const changes = steps.map(step => step.change);

        steps.forEach((step, index) => {
            this._later(at, () =>
                status({
                    stage: 'applying',
                    changeIndex: index,
                    changes: index === 0 ? changes : undefined,
                })
            );
            at += MOCK_TIMING.beforeApply;
            this._later(at, () => step.apply());
            at += MOCK_TIMING.betweenChanges;
        });

        at += MOCK_TIMING.done;
        this._later(at, () =>
            status({
                stage: 'done',
                changes: changes.map(change => ({ ...change, status: 'applied' })),
                reply: this._t(steps.length ? 'mock.reply' : 'mock.replyNothing'),
                suggestions: steps.length
                    ? [this._t('mock.suggestionTotal'), this._t('mock.suggestionShow')]
                    : [],
            })
        );

        return taskId;
    }

    /** Desfaz em lote: as alterações voltam na ordem inversa, pelos mesmos eventos. */
    public revert(taskId: string): void {
        const run = this._runs.get(taskId);
        if (!run) return;

        this._runs.delete(taskId);
        [...run.steps].reverse().forEach((step, index) => {
            this._later(MOCK_TIMING.revertStep * (index + 1), () => step.revert());
        });
    }

    // ─────────────────────────────────────────────────────────── plano

    private _plan(): MockStep[] {
        const steps: MockStep[] = [];
        const sessions = this._contentSessions();
        const textTarget = this._firstText(sessions);

        if (textTarget) {
            const step = this._shortenTextStep(textTarget.session, textTarget.item);
            if (step) steps.push(step);
        }

        const target = textTarget?.session ?? sessions[0];
        if (target) {
            steps.push(this._createTableStep(target));

            // Seção já renomeada por um pedido anterior: não empilha o sufixo de novo.
            const suffix = this._t('mock.renamedSection', { name: '' }).trim();
            if (!target.name?.trim().endsWith(suffix)) {
                steps.push(this._renameSessionStep(target, sessions.indexOf(target) + 1));
            }
        }

        return steps;
    }

    /** Alteração 1: o primeiro texto do documento fica só com a primeira frase. */
    private _shortenTextStep(
        session: DocumentSessionPartial,
        item: DocumentItemPartial
    ): MockStep | null {
        const shortened = this._shortenDelta(item.value);
        if (!shortened) return null;

        const before = { ...item };
        const change = this._change('update_text', 'mock.changeShorten', 'mock.whereText', {
            fieldIds: [item._id],
            sessionId: session._id,
            params: { section: session.name },
        });

        return {
            change,
            apply: () => this._emitFieldUpdated(session._id, { ...item, value: shortened }),
            revert: () => this._emitFieldUpdated(session._id, before),
        };
    }

    /** Alteração 2: um parágrafo curto + a tabela de etapas, no fim da seção. */
    private _createTableStep(session: DocumentSessionPartial): MockStep {
        const intro = new DocumentItemPartial();
        intro._id = mockId();
        intro.type = DocumentItemTypeEnum.TEXT;
        intro.value = JSON.stringify({ ops: [{ insert: `${this._t('mock.tableIntro')}\n` }] });
        intro.rev = 1;

        const table = new DocumentItemPartial();
        table._id = mockId();
        table.type = DocumentItemTypeEnum.TABLE;
        table.value = {
            columns: this._t('mock.tableColumns').split('|'),
            rows: this._t('mock.tableRows')
                .split(';')
                .map(row => row.split('|')),
        };
        table.configs = {
            tableStyle: DEFAULT_TABLE_STYLE,
            tableZebra: DEFAULT_TABLE_ZEBRA,
            calloutColor: DEFAULT_CALLOUT_COLOR,
        };
        table.rev = 1;

        const change = this._change('create_items', 'mock.changeCreateTable', 'mock.whereNewTable', {
            fieldIds: [intro._id, table._id],
            sessionId: session._id,
            params: { section: session.name },
        });

        return {
            change,
            apply: () => {
                const count = this._sessionNow(session._id)?.items?.length ?? 0;
                this._pusher.simulate(this._channel(), 'fields-created', {
                    fields: [
                        { value: intro, index: count },
                        { value: table, index: count + 1 },
                    ],
                    sessionId: session._id,
                    sessionSize: this._sessionNow(session._id)?.size ?? session.size,
                });
            },
            revert: () =>
                this._pusher.simulate(this._channel(), 'fields-deleted', {
                    fieldsIds: [intro._id, table._id],
                    sessionId: session._id,
                    sessionSize: this._sessionNow(session._id)?.size ?? session.size,
                }),
        };
    }

    /** Alteração 3: a seção ganha um nome que diz o que ela tem agora. */
    private _renameSessionStep(session: DocumentSessionPartial, position: number): MockStep {
        const previous = session.name;
        const name = this._t('mock.renamedSection', { name: previous });

        const change = this._change('rename_section', 'mock.changeRename', 'mock.whereSectionTitle', {
            fieldIds: [],
            sessionId: session._id,
            params: { name, index: position },
        });

        const emit = (value: string): void =>
            this._pusher.simulate(this._channel(), 'session-name-updated', {
                name: value,
                sessionId: session._id,
            });

        return { change, apply: () => emit(name), revert: () => emit(previous) };
    }

    // ─────────────────────────────────────────────────────── utilidades

    private _change(
        kind: AiEditChange['kind'],
        labelKey: string,
        whereKey: string,
        options: { fieldIds: string[]; sessionId: string; params: Record<string, unknown> }
    ): AiEditChange {
        return {
            id: mockId(),
            kind,
            label: this._t(labelKey, options.params),
            where: this._t(whereKey, options.params),
            fieldIds: options.fieldIds,
            sessionId: options.sessionId,
            status: 'pending',
        };
    }

    private _emitFieldUpdated(sessionId: string, field: DocumentItemPartial): void {
        // A revisão só cresce: parte da que está na tela agora, não da do plano.
        const current = this._editData.findFieldAnywhere(field._id);
        const rev = (current?.field?.rev ?? field.rev ?? 0) + 1;

        this._pusher.simulate(this._channel(), 'field-updated', {
            field: { ...field, rev },
            sessionId,
            sessionSize: this._sessionNow(sessionId)?.size ?? 0,
        });
    }

    /**
     * Encurta um Delta do Quill até o fim da primeira frase, preservando os `dataTag`s que
     * vierem antes do corte (regra do doc: a IA nunca troca uma tag por texto literal).
     */
    private _shortenDelta(raw: unknown): string | null {
        let delta: { ops?: { insert?: unknown; attributes?: unknown }[] };
        try {
            delta = typeof raw === 'string' ? JSON.parse(raw) : (raw as typeof delta);
        } catch {
            return null;
        }

        const ops = delta?.ops ?? [];
        const plain = ops.map(op => (typeof op.insert === 'string' ? op.insert : ' ')).join('');
        if (plain.trim().length < 80) return null;

        const kept: { insert?: unknown; attributes?: unknown }[] = [];
        let length = 0;

        for (const op of ops) {
            if (typeof op.insert !== 'string') {
                kept.push(op);
                length += 1;
                continue;
            }

            const match = /[.!?](\s|$)/.exec(op.insert);
            const cut = match && length + match.index >= 40 ? match.index + 1 : -1;

            if (cut === -1) {
                kept.push(op);
                length += op.insert.length;
                continue;
            }

            kept.push({ ...op, insert: op.insert.slice(0, cut) });
            break;
        }

        kept.push({ insert: '\n' });
        const shortened = JSON.stringify({ ops: kept });
        return shortened === JSON.stringify(delta) ? null : shortened;
    }

    private _firstText(
        sessions: DocumentSessionPartial[]
    ): { session: DocumentSessionPartial; item: DocumentItemPartial } | null {
        for (const session of sessions) {
            const item = (session.items ?? []).find(
                candidate =>
                    candidate.type === DocumentItemTypeEnum.TEXT &&
                    !candidate.parentFolderId &&
                    !!this._shortenDelta(candidate.value)
            );
            if (item) return { session, item };
        }
        return null;
    }

    /** Seções de conteúdo (sem a de assinaturas). */
    private _contentSessions(): DocumentSessionPartial[] {
        return (this._editData.document()?.sessions ?? []).filter(
            session => session.type !== DocumentSessionTypeEnum.SIGNATURE
        );
    }

    private _sessionNow(sessionId: string): DocumentSessionPartial | undefined {
        return this._editData.document()?.sessions?.find(session => session._id === sessionId);
    }

    private _channel(): string {
        return `DOCUMENT-${this._editData.document()?._id}`;
    }

    private _later(ms: number, fn: () => void): void {
        this._timers.push(setTimeout(fn, ms));
    }

    private _t(key: string, params?: Record<string, unknown>): string {
        return this._translate.instant(`document.edit.aiEdit.${key}`, params);
    }

    private _currentUserId(): string | undefined {
        try {
            return this._userController.getCurrentUserId();
        } catch {
            return undefined;
        }
    }
}
